"use client";

import { useEffect, useState, type ReactNode } from "react";
import {
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  ExternalLink,
  FileSpreadsheet,
  Link2,
  RefreshCw,
} from "lucide-react";
import { Modal, ModalCloseButton } from "@aera/ui";
import { LineLogo } from "@/app/components/LineLogo";
import { lineApi } from "@/lib/api";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import { navigateToGoogleIntegrationsConnect } from "@/lib/googleIntegrationsConnect";
import {
  getGoogleConnectionPresentation,
  getGoogleSheetUrl,
  type GoogleIntegrationStatus,
} from "@/lib/googleIntegrationStatus";
import { fetchGoogleIntegrationStatus } from "@/lib/googleIntegrationStatusFetch";
import {
  DEFAULT_BILLIN_LINE_DEEP_LINK,
  type LineBillinBindStatus,
} from "@/hooks/useLineBillinDeepLink";

const SOURCE_LABEL = "LINE OA · Google Sheets";
const SOURCE_COUNT = 2;

type DataSourcesStatusControlProps = {
  workspaceId?: string | null;
  workspaceName?: string | null;
  serviceId?: string | null;
  size?: "sm" | "md";
};

function useDataSourcesStatus(
  isActive: boolean,
  workspaceId?: string | null,
  serviceId?: string | null,
) {
  const [lineStatus, setLineStatus] = useState<LineBillinBindStatus | null>(null);
  const [googleStatus, setGoogleStatus] = useState<GoogleIntegrationStatus | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    const id = serviceId?.trim() || BILLIN_SERVICE_ID;
    setLoading(true);
    void Promise.all([
      lineApi.getBindStatus(id).catch(() => ({
        bound: false,
        deepLink: DEFAULT_BILLIN_LINE_DEEP_LINK,
      })),
      fetchGoogleIntegrationStatus({ force: true }).catch(() => ({
        connected: null,
        sheetsByWorkspace: {},
      })),
    ]).then(([line, google]) => {
      if (cancelled) return;
      setLineStatus(line as LineBillinBindStatus);
      setGoogleStatus(google);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isActive, serviceId, workspaceId]);

  const lineReady = Boolean(lineStatus?.bound);
  const googlePresentation = getGoogleConnectionPresentation(
    googleStatus,
    workspaceId,
  );
  const googleReady =
    googlePresentation.tone === "connected" ||
    googlePresentation.tone === "pending-link";
  const readyCount = Number(lineReady) + Number(googleReady);
  const sheetUrl = getGoogleSheetUrl(googleStatus, workspaceId);
  const lineHref =
    lineStatus?.deepLink?.trim() || DEFAULT_BILLIN_LINE_DEEP_LINK;

  return {
    lineStatus,
    googleStatus,
    loading,
    lineReady,
    googlePresentation,
    googleReady,
    readyCount,
    sheetUrl,
    lineHref,
  };
}

function SourceStatusMark({
  ok,
  label,
}: {
  ok: boolean;
  label: string;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 text-xs font-medium ${
        ok ? "text-aera-green" : "text-aera-amberdark"
      }`}
    >
      {ok ? (
        <CheckCircle2 className="h-4 w-4" strokeWidth={2} aria-hidden />
      ) : (
        <CircleAlert className="h-4 w-4" strokeWidth={2} aria-hidden />
      )}
      {label}
    </span>
  );
}

function SourceCardSkeleton() {
  return (
    <div
      className="rounded-2xl border border-aera-lightgray bg-aera-white p-3.5"
      aria-hidden
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-aera-lightgray/60" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="h-4 w-20 animate-pulse rounded bg-aera-lightgray/70" />
            <div className="h-4 w-24 animate-pulse rounded bg-aera-lightgray/60" />
          </div>
          <div className="mt-2 h-3 w-32 animate-pulse rounded bg-aera-lightgray/50" />
        </div>
      </div>
      <div className="mt-3 h-10 w-full animate-pulse rounded-xl bg-aera-offwhite" />
    </div>
  );
}

function SourceActionButton({
  label,
  icon,
  href,
  onClick,
  disabled,
}: {
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const className =
    "mt-3 flex w-full cursor-pointer items-center justify-between rounded-xl bg-aera-offwhite px-3 py-2.5 text-sm text-aera-charcoal disabled:cursor-not-allowed disabled:opacity-50";

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        <span>{label}</span>
        <span className="text-aera-gray" aria-hidden>
          {icon}
        </span>
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
    >
      <span>{label}</span>
      <span className="text-aera-gray" aria-hidden>
        {icon}
      </span>
    </button>
  );
}

function DataSourcesCards(props: {
  loading: boolean;
  lineReady: boolean;
  googleReady: boolean;
  googlePresentation: ReturnType<typeof getGoogleConnectionPresentation>;
  lineHref: string;
  sheetUrl: string | null;
  connectingGoogle: boolean;
  onGoogleAction: () => void;
  testIdPrefix?: string;
}) {
  const prefix = props.testIdPrefix ? `${props.testIdPrefix}-` : "";
  const googleSubtitle =
    props.googlePresentation.tone === "needs-reconnect"
      ? "สิทธิ์เดิมใช้ไม่ได้แล้ว"
      : props.googlePresentation.tone === "connected"
        ? "รับข้อมูลใหม่อยู่"
        : props.googlePresentation.label;

  if (props.loading) {
    return (
      <>
        <SourceCardSkeleton />
        <SourceCardSkeleton />
      </>
    );
  }

  return (
    <>
      <div
        className="rounded-2xl border border-aera-lightgray bg-aera-white p-3.5"
        data-testid={`${prefix}line-card`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-greenlight text-aera-green">
            <LineLogo mode="mark" className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-aera-charcoal">LINE OA</p>
              <SourceStatusMark
                ok={props.lineReady}
                label={props.lineReady ? "เชื่อมต่อแล้ว" : "ยังไม่ได้ผูก"}
              />
            </div>
            <p className="mt-0.5 text-xs text-aera-gray">
              {props.lineReady ? "รับข้อมูลใหม่อยู่" : "ยังไม่ได้ผูกบัญชี"}
            </p>
          </div>
        </div>
        <SourceActionButton
          label={props.lineReady ? "เปิด LINE" : "เชื่อมต่อ"}
          icon={<ExternalLink className="h-4 w-4" strokeWidth={2} />}
          href={props.lineHref || undefined}
        />
      </div>

      <div
        className="rounded-2xl border border-aera-lightgray bg-aera-white p-3.5"
        data-testid={`${prefix}google-card`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-amberlight text-aera-amberdark">
            <FileSpreadsheet className="h-5 w-5" strokeWidth={2} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-aera-charcoal">
                Google Sheets
              </p>
              <SourceStatusMark
                ok={props.googleReady}
                label={
                  props.googleReady
                    ? "เชื่อมต่อแล้ว"
                    : props.googlePresentation.tone === "disconnected"
                      ? "ยังไม่ได้เชื่อมต่อ"
                      : "ต้องเชื่อมใหม่"
                }
              />
            </div>
            <p className="mt-0.5 text-xs text-aera-gray">{googleSubtitle}</p>
          </div>
        </div>
        <SourceActionButton
          label={
            props.connectingGoogle
              ? "กำลังเชื่อมต่อ..."
              : props.googleReady && props.sheetUrl
                ? "เปิดชีต"
                : props.googlePresentation.tone === "needs-reconnect"
                  ? "เชื่อมใหม่"
                  : "เชื่อมต่อ"
          }
          icon={
            props.googleReady && props.sheetUrl ? (
              <ExternalLink className="h-4 w-4" strokeWidth={2} />
            ) : (
              <RefreshCw className="h-4 w-4" strokeWidth={2} />
            )
          }
          onClick={props.onGoogleAction}
          disabled={props.connectingGoogle}
        />
      </div>
    </>
  );
}

function DataSourcesStatusModal({
  isOpen,
  onClose,
  workspaceId,
  workspaceName,
  serviceId,
}: {
  isOpen: boolean;
  onClose: () => void;
  workspaceId?: string | null;
  workspaceName?: string | null;
  serviceId?: string | null;
}) {
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const {
    loading,
    lineReady,
    googlePresentation,
    googleReady,
    readyCount,
    sheetUrl,
    lineHref,
  } = useDataSourcesStatus(isOpen, workspaceId, serviceId);
  const title = (workspaceName || "ร้าน").trim();

  const handleGoogleAction = () => {
    if (googleReady && sheetUrl) {
      window.open(sheetUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (typeof window === "undefined") return;
    setConnectingGoogle(true);
    navigateToGoogleIntegrationsConnect({
      serviceId: serviceId?.trim() || BILLIN_SERVICE_ID,
      callbackUrl: `${window.location.pathname}${window.location.search}`,
      auto: true,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="center"
      hideCloseButton
      header={
        <div className="relative px-5 pb-1 pt-5">
          <div className="absolute right-3 top-3">
            <ModalCloseButton onClose={onClose} />
          </div>
          <p className="text-xs font-medium text-aera-amberdark">ข้อมูลที่ใช้</p>
          <h2 className="mt-1 pr-10 text-xl font-bold tracking-tight text-aera-charcoal">
            {title}
          </h2>
          {loading ? (
            <div className="mt-1.5 h-4 w-44 animate-pulse rounded bg-aera-lightgray/60" />
          ) : (
            <p className="mt-0.5 text-sm text-aera-gray">
              {`${readyCount} จาก ${SOURCE_COUNT} แหล่งพร้อมใช้งาน`}
            </p>
          )}
        </div>
      }
      bodyClassName="px-5 py-4"
    >
      <div className="flex flex-col gap-3" aria-busy={loading}>
        <DataSourcesCards
          loading={loading}
          lineReady={lineReady}
          googleReady={googleReady}
          googlePresentation={googlePresentation}
          lineHref={lineHref}
          sheetUrl={sheetUrl}
          connectingGoogle={connectingGoogle}
          onGoogleAction={handleGoogleAction}
        />

        <p className="flex items-center gap-1.5 pt-1 text-xs text-aera-gray">
          <Link2 className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
          เชื่อมต่อได้จากรายการตัวแทน
        </p>
      </div>
    </Modal>
  );
}

export function DataSourcesStatusControl({
  workspaceId,
  workspaceName,
  serviceId,
  size = "md",
}: DataSourcesStatusControlProps) {
  const [open, setOpen] = useState(false);
  const compact = size === "sm";

  return (
    <>
      <button
        type="button"
        className={`inline-flex min-w-0 shrink-0 cursor-pointer items-center gap-1.5 text-aera-gray ${
          compact ? "text-xs" : "text-sm"
        }`}
        aria-label="แหล่งข้อมูล"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span
          className={`shrink-0 rounded-full bg-aera-green ${
            compact ? "h-1.5 w-1.5" : "h-2 w-2"
          }`}
        />
        <span className={compact ? "truncate" : "whitespace-nowrap"}>
          {SOURCE_LABEL}
        </span>
        <ChevronDown
          className={`shrink-0 ${compact ? "h-3.5 w-3.5" : "h-4 w-4"}`}
          aria-hidden
        />
      </button>
      <DataSourcesStatusModal
        isOpen={open}
        onClose={() => setOpen(false)}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        serviceId={serviceId}
      />
    </>
  );
}
